import { useEffect, useRef, useState, useCallback } from 'react'
import { Application } from 'pixi.js'
import { SavasOturumu } from '@kurultay/savas'
import { meslegeGoreDuruslar } from '@kurultay/tipler'
import type { SavasOlayi, KahramanKomutu, Kurulum } from '@kurultay/savas'
import { SavasSahnesi } from './sahne.js'
import { adimlariUret } from './oynatici.js'
import { raporUret } from './rapor.js'
import type { SavasRaporu } from './rapor.js'
import { Rapor } from './Rapor.js'

const OYUNCU_TARAFI = 'a' as const
/**
 * Oyuncu bu süre içinde beceri seçmezse yapay zekâ onun yerine oynar.
 * Kısa tutuldu: savaş akıcı kalmalı, bekleyen tur oyunu ağırlaştırıyor.
 * Bilerek oynayan zaten tuşa basıyor; basmayan beklemek istemiyordur.
 */
const TUR_SURESI_MS = 3000
const bekle = (ms: number) => new Promise((c) => setTimeout(c, ms))

export function SavasEkrani({ kurulum, tohum, fon, tekrar }: {
  kurulum: Kurulum
  tohum: number
  fon: string
  tekrar: () => void
}) {
  const tuvalRef = useRef<HTMLDivElement>(null)
  const sahneRef = useRef<SavasSahnesi | null>(null)
  const oturumRef = useRef<SavasOturumu | null>(null)
  const appRef = useRef<Application | null>(null)

  const [hazir, setHazir] = useState(false)
  const [oynatiliyor, setOynatiliyor] = useState(false)
  /**
   * `oynatiliyor` React durumu ve güncellenmesi asenkron. Otomatik ilerleme
   * efekti art arda tetiklenirse ikinci çağrı hâlâ eski `false` değerini
   * görüp aynı anda ikinci bir tur başlatabilir. Ref eşzamanlı olduğu için
   * gerçek kilit budur; state yalnızca arayüzü çizmek için.
   */
  const oynatiliyorRef = useRef(false)
  const [hiz, setHiz] = useState(1)
  const [tur, setTur] = useState(1)
  const [ofke, setOfke] = useState(0)
  const [canOrani, setCanOrani] = useState(1)
  const [canDeger, setCanDeger] = useState(0)
  const [durus, setDurus] = useState<string>('')
  const [rapor, setRapor] = useState<SavasRaporu | null>(null)
  const [gunluk, setGunluk] = useState<string[]>([])
  const [otomatik, setOtomatik] = useState(false)
  const [kalanMs, setKalanMs] = useState(TUR_SURESI_MS)

  // Pixi uygulamasını ve oturumu kur
  useEffect(() => {
    let iptal = false
    const app = new Application()
    ;(async () => {
      await app.init({ background: 0x14110d, resizeTo: tuvalRef.current!, antialias: true })
      if (iptal) { app.destroy(true); return }
      tuvalRef.current!.appendChild(app.canvas)
      appRef.current = app

      const oturum = new SavasOturumu(kurulum, tohum, OYUNCU_TARAFI)
      const sahne = new SavasSahnesi(app)
      sahne.birimleriKur(oturum.durum, OYUNCU_TARAFI)
      void sahne.fonuYukle(fon)

      oynatiliyorRef.current = false
      oturumRef.current = oturum
      sahneRef.current = sahne
      setOfke(oturum.oyuncuKahramani.ofke)
      setDurus(oturum.oyuncuKahramani.durus)
      setCanOrani(1)
      setCanDeger(oturum.oyuncuKahramani.can)
      setHazir(true)
    })()

    return () => {
      iptal = true
      sahneRef.current?.yokEt()
      appRef.current?.destroy(true)
      sahneRef.current = null
      oturumRef.current = null
      appRef.current = null
    }
  }, [kurulum, tohum, fon])

  /** Olayları sahnede animasyonla oynatır. */
  const olaylariOynat = useCallback(async (olaylar: SavasOlayi[]) => {
    const sahne = sahneRef.current
    const oturum = oturumRef.current
    if (!sahne || !oturum) return

    for (const { olay, gecikmeMs } of adimlariUret(olaylar, hiz)) {
      switch (olay.tip) {
        case 'tur_basladi':
          setTur(olay.tur)
          break
        case 'durus_degisti': {
          const b = oturum.durum.birimler.find((x) => x.id === olay.birimId)
          if (b) sahne.safGuncelle(b.id, b.taraf, b.saf)
          setGunluk((g) => [`${olay.birimId} → ${olay.yeniDurus}`, ...g].slice(0, 6))
          break
        }
        case 'saldiri': {
          sahne.hasarGoster(olay.hedefId, olay.hasar, olay.hasarTuru, olay.kritik, olay.isabetEtti)
          const h = oturum.durum.birimler.find((x) => x.id === olay.hedefId)
          if (h) sahne.birimGuncelle(h.id, h.can, h.azamiCan)
          break
        }
        case 'olum':
          sahne.birimOldur(olay.birimId)
          setGunluk((g) => [`${olay.birimId} düştü`, ...g].slice(0, 6))
          break
      }
      if (gecikmeMs > 0) await bekle(gecikmeMs)
    }

    const kahraman = oturum.oyuncuKahramani
    setOfke(oturum.durum.bitti ? 0 : kahraman.ofke)
    setCanDeger(kahraman.can)
    setCanOrani(kahraman.azamiCan > 0 ? kahraman.can / kahraman.azamiCan : 0)
    if (!oturum.durum.bitti) setDurus(kahraman.durus)
  }, [hiz])

  const turOyna = useCallback(async (komutlar: KahramanKomutu[]) => {
    const oturum = oturumRef.current
    if (!oturum || oynatiliyorRef.current || oturum.bitti) return
    oynatiliyorRef.current = true
    setOynatiliyor(true)
    try {
      const olaylar = oturum.turuOyna(komutlar)
      await olaylariOynat(olaylar)
      setKalanMs(TUR_SURESI_MS)
      if (oturum.bitti) setRapor(raporUret(oturum.kaydiAl()))
    } finally {
      oynatiliyorRef.current = false
      setOynatiliyor(false)
    }
  }, [olaylariOynat])

  const durusDegistirVeVur = useCallback(() => {
    const oturum = oturumRef.current
    if (!oturum || oynatiliyorRef.current || oturum.bitti || !oturum.durusDegistirilebilir()) return
    // Duruş değişince eski duruşun becerileri geçersiz; temel beceriye düşer.
    void turOyna([{ tip: 'durus_degistir' }])
  }, [turOyna])

  /**
   * Oyun hiçbir koşulda oyuncuyu beklemez:
   * - Kahraman düştüyse komut verecek kimse yok → otomatik sürer
   * - "Otomatik" açıksa → yapay zekâ oynar
   * - Süre dolduysa → yapay zekâ oynar
   * Üçü de boş komutla `turuOyna`'ya düşer; boş komut "sen karar ver" demektir.
   */
  useEffect(() => {
    const oturum = oturumRef.current
    if (!hazir || !oturum || oynatiliyor || oynatiliyorRef.current || oturum.bitti || rapor) return

    const kendiliginden = otomatik || oturum.kahramanDustu
    if (kendiliginden) {
      const z = setTimeout(() => void turOyna([]), 350)
      return () => clearTimeout(z)
    }

    // Elle oynanıyor: geri sayım
    const baslangic = Date.now()
    const sayac = setInterval(() => {
      const kalan = TUR_SURESI_MS - (Date.now() - baslangic)
      if (kalan <= 0) {
        clearInterval(sayac)
        setKalanMs(0)
        void turOyna([])   // süre doldu, yapay zekâ oynasın
      } else {
        setKalanMs(kalan)
      }
    }, 100)
    return () => clearInterval(sayac)
  }, [hazir, oynatiliyor, otomatik, rapor, turOyna, tur])

  const beceriKullan = useCallback((sira: number) => {
    const oturum = oturumRef.current
    if (!oturum || oynatiliyorRef.current || oturum.bitti) return
    const beceri = oturum.tumBeceriler()[sira]
    if (!beceri || !oturum.karsilanabilir(beceri)) return
    void turOyna([{ tip: 'beceri', beceriKodu: beceri.kod }])
  }, [turOyna])

  // Tab → duruş değiştir · 1-9 → beceri
  useEffect(() => {
    const dinle = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        e.preventDefault()
        durusDegistirVeVur()
        return
      }
      const sayi = Number(e.key)
      if (Number.isInteger(sayi) && sayi >= 1 && sayi <= 9) {
        e.preventDefault()
        beceriKullan(sayi - 1)
      }
    }
    window.addEventListener('keydown', dinle)
    return () => window.removeEventListener('keydown', dinle)
  }, [durusDegistirVeVur, beceriKullan])

  const oturum = oturumRef.current
  const beceriler = hazir && oturum && !oturum.bitti ? oturum.tumBeceriler() : []
  const kahraman = hazir && oturum && !oturum.bitti ? oturum.oyuncuKahramani : null
  const digerDurus = kahraman
    ? meslegeGoreDuruslar(kahraman.meslek).find((d: string) => d !== kahraman.durus)
    : null

  return (
    <div className="savas">
      <div className="tuval" ref={tuvalRef} />

      <div className="ust-bar">
        <span className="rozet">Tur {tur}</span>
        <span className="rozet">Duruş: <b>{durus}</b></span>
        <div className="hiz">
          <button
            className={otomatik ? 'secili oto' : 'oto'}
            onClick={() => setOtomatik((o) => !o)}
            title="Yapay zekâ senin yerine oynasın"
          >
            Otomatik
          </button>
          {[1, 2, 4].map((h) => (
            <button key={h} className={hiz === h ? 'secili' : ''} onClick={() => setHiz(h)}>
              ×{h}
            </button>
          ))}
        </div>
      </div>

      {gunluk.length > 0 && (
        <ul className="gunluk">{gunluk.map((s, i) => <li key={i}>{s}</li>)}</ul>
      )}

      {!rapor && !oynatiliyor && !otomatik && oturum && !oturum.kahramanDustu && (
        <div className="geri-sayim">
          <div style={{ width: `${(kalanMs / TUR_SURESI_MS) * 100}%` }} />
        </div>
      )}

      {!rapor && (otomatik || oturum?.kahramanDustu) && (
        <div className="oto-bilgi">
          {oturum?.kahramanDustu
            ? 'Kahramanın düştü — çerilerin savaşmayı sürdürüyor'
            : 'Otomatik savaş'}
        </div>
      )}

      {!rapor && (
        <div className="alt-bar">
          <div className="kure can-kure" title="Kahramanın canı">
            <div className="kure-dolgu" style={{ height: `${canOrani * 100}%` }} />
            <span>{canDeger}</span>
          </div>

          <button
            className="durus-dugme"
            disabled={oynatiliyor || !oturum?.durusDegistirilebilir()}
            onClick={durusDegistirVeVur}
            title="Tab"
          >
            ⇄ {digerDurus ?? '—'} <span className="tus">Tab</span>
          </button>

          <div className="beceriler">
            {beceriler.map((b, i) => {
              const acik = oturum?.karsilanabilir(b) ?? false
              return (
                <button
                  key={b.kod}
                  className={acik ? '' : 'kilitli'}
                  disabled={oynatiliyor || !acik}
                  onClick={() => beceriKullan(i)}
                  title={`${i + 1} tuşu`}
                >
                  <span className="b-ust">
                    <kbd>{i + 1}</kbd>
                    <span className="b-ad">{b.ad}</span>
                  </span>
                  <span className="b-bilgi">
                    ×{b.carpan}
                    {b.hedefSayisi > 1 && ` · ${b.hedefSayisi} hedef`}
                    {b.safDeler && ' · saf deler'}
                    {b.ofkeMaliyeti > 0 && ` · ${b.ofkeMaliyeti} öfke`}
                  </span>
                </button>
              )
            })}
          </div>

          <div className="kure ofke-kure" title="Öfke">
            <div className="kure-dolgu" style={{ height: `${ofke}%` }} />
            <span>{ofke}</span>
          </div>
        </div>
      )}

      {rapor && <Rapor rapor={rapor} oyuncuTarafi={OYUNCU_TARAFI} tekrar={tekrar} />}
    </div>
  )
}
